function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import { compact } from 'lodash-es';
import { Accounting } from 'anchor-scout-common/types';
import { isFutureEffectiveDate } from "../../../../business-logic/billingLogic";
import { useStore } from "../../../../contexts";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { getAccountingDetails } from "../../../../integrations/Accounting/accounting.utils";
import { serviceBillingModel, serviceCostModel, serviceModel } from "../../../../models";
import { selectors } from "../../../../store/state-selectors";
import { AdvancedSetting } from "../../ServiceAdvancedSettings.types";
const accountingName = getAccountingDetails(Accounting.QBO).fullName;
export const useGetSettings = _ref => {
  let {
    isFreshlyAdded,
    isApprovedRelationship,
    service,
    now,
    effectiveDate
  } = _ref;
  const {
    PREAPPROVED_AMOUNT_IMPROVMENTS,
    START_BILLING_AT,
    START_BILLING_AT_PHASE_2_INLINE,
    SERVICE_SETTINGS_SELECTORS = false
  } = useFeatureFlags();
  const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
  const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const hasQboClasses = useStore(state => state.qboClasses.data);
  const allowToCustomizeQboClasses = useStore(selectors.integration.allowToCustomizeQboClasses);
  return useMemo(() => {
    const isDraft = !isApprovedRelationship;
    const isFreshlyAddedOrDraft = isDraft || isFreshlyAdded;
    const isOngoing = serviceBillingModel.isOngoing(serviceModel.billingTrigger(service));
    const isBilledUpfront = serviceModel.isBilledUpfront(service);
    const isSkipBillingOnAcceptance = serviceModel.isSkipBillingOnAcceptance(service);
    const isRecurring = serviceBillingModel.isRecurring(serviceModel.billingTrigger(service));
    const isProrated = serviceModel.isProrated(service);
    const isPaused = serviceModel.isPaused(service);
    const isAwaitingApproval = serviceModel.isAwaitingApproval(service.status);
    const hasFinancialCentsIntegration = serviceModel.hasFinancialCentsIntegration(service);
    const hasKarbonIntegration = serviceModel.hasKarbonIntegration(service);
    const startBillingAt = serviceModel.startBillingAt(service);
    const isEffectiveDateInFuture = isFutureEffectiveDate(effectiveDate, now);
    let canProrate;
    let prorateIsDisabled;
    if (START_BILLING_AT || START_BILLING_AT_PHASE_2_INLINE) {
      canProrate = !(isBilledUpfront && isSkipBillingOnAcceptance) && !isPaused || !!startBillingAt;
    } else {
      prorateIsDisabled = isPaused;
      canProrate = !(isBilledUpfront && isSkipBillingOnAcceptance);
    }
    const startBillingAtFFEnabled = START_BILLING_AT || START_BILLING_AT_PHASE_2_INLINE;
    return compact([!startBillingAtFFEnabled && isFreshlyAddedOrDraft && isAwaitingApproval && isOngoing && {
      type: AdvancedSetting.Pause,
      label: 'Do not start automatic billing',
      info: 'The service will not be billed until you manually resume automatic billing for it.',
      disabled: isBilledUpfront ? !isSkipBillingOnAcceptance : isProrated,
      disabledTooltip: isBilledUpfront ? 'Services set to issue an invoice on proposal acceptance cannot be paused' : 'To pause automatic billing, remove proration',
      optionName: 'Do not start automatic billing'
    }, START_BILLING_AT && !START_BILLING_AT_PHASE_2_INLINE && isFreshlyAddedOrDraft && isAwaitingApproval && isOngoing && {
      type: AdvancedSetting.StartAutomaticBillingAt,
      label: 'Choose when automatic billing starts',
      info: 'Select when Anchor should begin issuing invoices for this service. You can delay billing, start on a specific date, or have it begin right when the proposal is accepted.',
      disabled: false,
      disabledTooltip: '',
      optionName: 'Choose when automatic billing starts'
    }, !startBillingAtFFEnabled && isFreshlyAddedOrDraft && isAwaitingApproval && isOngoing && isBilledUpfront && _objectSpread(_objectSpread({
      type: AdvancedSetting.BillOnApproval
    }, isApprovedRelationship ? {
      label: 'Issue an invoice immediately',
      info: 'An invoice for this service will be issued immediately once amendments are saved in addition to the invoices issued on the upcoming billing date and going forward. Invoices including this service will require an approval from your client until this amendment is approved.',
      optionName: 'Issue an invoice immediately'
    } : isEffectiveDateInFuture ? {
      label: 'Charge this service on the effective date of the agreement',
      info: "The service will be charged on the effective date of the agreement (even if it's not the set billing date) and continue issuing from the next scheduled billing date and going forward.",
      optionName: 'Bill on effective date'
    } : {
      label: 'Charge this service on proposal acceptance',
      info: "The service will be charged on the day the proposal is accepted (even if it's not the set billing date) and continue issuing from the next scheduled billing date and going forward.",
      optionName: 'Bill on proposal acceptance'
    }), {}, {
      disabled: isPaused,
      disabledTooltip: 'Services with automatic billing set to paused cannot be charged on acceptance'
    }), isFreshlyAddedOrDraft && isAwaitingApproval && isOngoing && canProrate && {
      type: AdvancedSetting.Prorate,
      label: 'Prorate the first payment',
      info: 'When enabled, the price of the first invoice will be calculated in proportion to the number of days the agreement was in effect during the billing cycle',
      disabled: prorateIsDisabled,
      disabledTooltip: 'Services with automatic billing set to paused cannot be prorated',
      optionName: 'First payment proration'
    }, isAwaitingApproval && !isApprovedRelationship && {
      type: AdvancedSetting.Optional,
      label: SERVICE_SETTINGS_SELECTORS ? 'Mark as optional' : 'Mark this service as optional',
      info: 'Mark a service as optional if you would like to give your client the option to include the service in this agreement',
      optionName: 'Optional service'
    }, isAwaitingApproval && isRecurring && {
      type: AdvancedSetting.LimitCharges,
      label: 'Bill a limited number of times',
      info: 'Set a limit on how many times this service can be billed',
      optionName: 'Number of billing cycles'
    }, serviceModel.supportsPriceIncrease(service) && {
      type: AdvancedSetting.AutoPriceIncrease,
      label: 'Set an automatic annual price increase',
      info: 'Automatic price increase will be applied once a year unless manually changed or cancelled.',
      optionName: 'Annual price increase'
    }, !isApprovedRelationship && {
      type: AdvancedSetting.LimitedTimeDiscountOffer,
      label: 'Apply discount only if the proposal is accepted within the deadline',
      disabled: !service.cost.discount,
      info: 'The discount specified for this service will apply only if the proposal is accepted before the deadline. If accepted after the deadline, the discount will be automatically removed',
      disabledTooltip: 'To set a condition for the discount, please add a discount to the service first.',
      optionName: 'Conditional discount'
    }, isAwaitingApproval && serviceBillingModel.isRecurring(service.billing) && {
      type: AdvancedSetting.LimitedTimeDiscountOfferForAgreement,
      label: 'Expire discount after a set period',
      disabled: !service.cost.discount,
      info: "Discount applies for the set period after signing, then it's removed automatically.",
      disabledTooltip: 'To set an automatic expiration for the discount, you must first add a discount to the service.',
      optionName: 'Discount expiration'
    }, showQboIntegration && {
      type: AdvancedSetting.QboClasses,
      label: "Override ".concat(accountingName, " class assignment"),
      info: "Set the class of this service when syncing invoices to ".concat(accountingName, "."),
      disabled: !allowToCustomizeQboClasses || !!(hasQboClasses && !hasQboClasses.length),
      disabledTooltip: "To override the ".concat(accountingName, " class assignment, classes must first be enabled and defined in ").concat(accountingName, "."),
      optionName: 'QuickBooks Online class override'
    }, showFinancialCentsIntegration && !isApprovedRelationship && {
      type: AdvancedSetting.FinancialCents,
      label: 'Override team member assignment in Financial Cents',
      info: 'Select the team members to be assigned to the items generated by this service in Financial Cents once the proposal has been approved.',
      disabled: !hasFinancialCentsIntegration,
      disabledTooltip: 'To override team member assignments, the service needs to be mapped to a template in Financial Cents.',
      optionName: 'Financial Cents assignee override'
    }, showKarbonIntegration && !isApprovedRelationship && {
      type: AdvancedSetting.Karbon,
      label: 'Override team member assignment in Karbon',
      info: 'Select the team members to be assigned to the items generated by this service in Karbon once the proposal has been approved.',
      disabled: !hasKarbonIntegration || !service.integrations.karbonIntegratedIds().length,
      disabledTooltip: 'To override team member assignments, the service needs to be mapped to a template in Karbon.',
      optionName: 'Karbon assignee override'
    }, PREAPPROVED_AMOUNT_IMPROVMENTS && serviceCostModel.isVariablePrice(service.cost) && {
      type: AdvancedSetting.PreapprovedAmount,
      label: 'Set a preapproved amount',
      info: "Charging a client up to the preapproved amount for the period doesn't require their approval. If no amount is set, or if the charge exceeds the limit, client approval will be required.",
      optionName: 'Preapproved amount'
    }]);
  }, [isFreshlyAdded, isApprovedRelationship, service, now, effectiveDate]);
};